import { useEffect, useState } from "react";

import { apiClient } from "../api/client";
import type { Member, Recipient } from "../api/types";

export interface MemberRecipientsState {
  status: "loading" | "ready" | "error";
  recipients: Recipient[];
  errorMessage: string | null;
}

const initial: MemberRecipientsState = { status: "loading", recipients: [], errorMessage: null };

// Members carry no avatar_url, so map to the UserPicker's Recipient shape with
// an empty avatar (the picker falls back to initials).
function memberToRecipient(member: Member): Recipient {
  return { id: member.id, email: member.email, name: member.name, avatar_url: "" };
}

// useMemberRecipients fetches the tenant member list mapped to the UserPicker's
// Recipient shape. Keyed on `enabled` so modal-style consumers fetch on open;
// cancellable so a re-open before the previous request returns doesn't clobber
// state.
export function useMemberRecipients(enabled: boolean): MemberRecipientsState {
  const [state, setState] = useState<MemberRecipientsState>(initial);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    setState({ ...initial });
    apiClient
      .listMembers()
      .then((response) => {
        if (cancelled) {
          return;
        }
        setState({
          status: "ready",
          recipients: response.members.map(memberToRecipient),
          errorMessage: null
        });
      })
      .catch((caught: unknown) => {
        if (cancelled) {
          return;
        }
        setState({
          status: "error",
          recipients: [],
          errorMessage: caught instanceof Error ? caught.message : "Could not load members."
        });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return state;
}
